import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_RECYCLE, POOL_PERIOD_LABEL, type Campaign, type CampaignDisposition, type NumberPool, type RecycleRules } from '@arcnet/contracts';
import {
  useCampaign, useCampaignAttempts, useCampaigns, useCampaignStatus, useDeleteCampaign, useDiallerLists,
  useSaveCampaign, useScripts,
} from '../../lib/api/hooks-dialler.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useNumbers, useQueues, useRingGroups, useIvrs } from '../../lib/api/hooks-tms.js';
import { api } from '../../lib/api/client.js';
import { formatMoney } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import { useSubroute } from '../../lib/subroute.js';
import { formatNumber, relativeTime } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, SectionHead, Segmented, Stat, StatGroup, SubHead, TableSkeleton, Tab, Tabs } from '../../components/ui/Primitives.js';
import { OrderedPicker } from '../../components/ui/OrderedPicker.js';
import { Toggle } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Campaigns (docs/36 D1b, 0127): which lists, who works them, when, and what happens to a lead
 * after each call. Preview campaigns are worked in the agent workspace; auto campaigns are dialled
 * by the switch (the node's dialler engine).
 *
 * A campaign is opened by its name in the address: /dialler/campaigns/march-renewals.
 */

const slugOf = (name: string): string =>
  name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'campaign';

export function slugs(items: ReadonlyArray<Pick<Campaign, 'id' | 'name' | 'created_at'>>): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Map<string, number>();
  for (const c of [...items].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    const base = slugOf(c.name);
    const n = (taken.get(base) ?? 0) + 1;
    taken.set(base, n);
    out.set(c.id, n === 1 ? base : `${base}-${n}`);
  }
  return out;
}

const STATUS_TONE: Record<Campaign['status'], 'ok' | 'warn' | 'muted' | 'info'> = {
  running: 'ok', paused: 'warn', draft: 'muted', finished: 'info',
};
const MODE_LABEL: Record<string, string> = { preview: 'Preview', auto: 'Auto', progressive: 'Progressive', power: 'Power', predictive: 'Predictive' };

/** A disposition's code from its label: "Appointment booked" -> appointment_booked, unique in the list. */
function codeOf(label: string, all: ReadonlyArray<{ code: string }>, self: number): string {
  let base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 26);
  if (!/^[a-z]/.test(base)) base = `c_${base}`;
  if (base.length < 2) base = 'outcome';
  let code = base; let n = 2;
  while (all.some((d, i) => i !== self && d.code === code)) code = `${base}_${n++}`;
  return code;
}

export function CampaignsPage({ tenantId, onReport }: { tenantId: string; onReport?: ((slug: string) => void) | undefined }) {
  const route = useSubroute();
  const campaigns = useCampaigns(tenantId);
  const can = useSession((s) => s.can);
  const all = campaigns.data?.data ?? [];
  const bySlug = slugs(all);
  const open = route.rest[0];

  if (open === 'new') {
    return (
      <>
        <Crumb onBack={() => route.go([])} />
        <SectionHead title="New campaign" />
        <Editor tenantId={tenantId} campaign={null} onSaved={(name) => route.go([slugOf(name)])} />
      </>
    );
  }
  if (open) {
    const c = all.find((x) => bySlug.get(x.id) === open) ?? null;
    return (
      <>
        <Crumb onBack={() => route.go([])} />
        {campaigns.data && !c && (
          <div className="arc-banner arc-banner-warn"><Icon name="warning" size={14} /><span>There is no campaign called {open}.</span></div>
        )}
        {c && <CampaignView tenantId={tenantId} id={c.id} onReport={onReport ? () => onReport(open) : undefined} onGone={() => route.go([])} onRenamed={(n) => route.go([slugOf(n)], { replace: true })} />}
      </>
    );
  }

  return (
    <>
      <SectionHead title="Campaigns" sub="Lead lists worked by agents (preview) or dialled by the switch with a message (auto)."
        action={can('campaign:write')
          ? <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => route.go(['new'])}>
              <Icon name="plus" size={13} /> Campaign
            </button>
          : undefined} />
      {campaigns.error && <ErrorBanner error={campaigns.error} />}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Campaign</th><th>Mode</th><th>Status</th><th className="arc-td-right">Ready now</th>
              <th className="arc-td-right">Done</th><th className="arc-td-right">Leads</th></tr></thead>
            {campaigns.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {all.map((c) => (
                  <tr key={c.id} className="arc-clickable" onClick={() => route.go([bySlug.get(c.id)!])}>
                    <td><span style={{ fontWeight: 570 }}>{c.name}</span>
                      {c.description && <div className="arc-subtle" style={{ fontSize: 11.5 }}>{c.description}</div>}</td>
                    <td>{MODE_LABEL[c.mode]}</td>
                    <td><Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge></td>
                    <td className="arc-td-right arc-num">{formatNumber(c.counts.due_now)}</td>
                    <td className="arc-td-right arc-num">{formatNumber(c.counts.done)}</td>
                    <td className="arc-td-right arc-num">{formatNumber(c.counts.total)}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {campaigns.data && all.length === 0 && <Empty icon="callOut" title="No campaigns" hint="Make a lead list first, then a campaign to work it." />}
      </div>
    </>
  );
}

function Crumb({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="arc-crumb" onClick={onBack}
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
      <Icon name="arrowLeft" size={15} /> Campaigns
    </button>
  );
}

// ================================================================================== one campaign

type ViewTab = 'overview' | 'settings' | 'calls';

function CampaignView({ tenantId, id, onGone, onRenamed, onReport }: {
  tenantId: string; id: string; onGone: () => void; onRenamed: (name: string) => void;
  /** Its analytics, which live under Reporting. */
  onReport?: (() => void) | undefined;
}) {
  const route = useSubroute();
  const can = useSession((s) => s.can);
  const q = useCampaign(tenantId, id);
  const setStatus = useCampaignStatus(tenantId);
  const del = useDeleteCampaign(tenantId);
  const tab: ViewTab = route.rest[1] === 'settings' ? 'settings' : route.rest[1] === 'calls' ? 'calls' : 'overview';
  const go = (t: ViewTab) => route.go([route.rest[0]!, ...(t === 'overview' ? [] : [t])]);
  const c = q.data;
  if (!c) return q.error ? <ErrorBanner error={q.error} /> : <TableSkeleton rows={3} cols={3} />;
  const writer = can('campaign:write');

  return (
    <>
      <SectionHead title={c.name}
        sub={`${MODE_LABEL[c.mode]} campaign · ${c.status}${c.started_at ? ` · started ${relativeTime(c.started_at)}` : ''}`}
        action={writer || onReport ? (
          <div className="arc-row" style={{ gap: 6 }}>
            {onReport && (
              <button type="button" className="arc-btn arc-btn-sm" onClick={onReport}><Icon name="activity" size={13} /> Analytics</button>
            )}
            {writer && c.status !== 'running' && c.status !== 'finished' && (
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id, status: 'running' })}>Start</button>
            )}
            {writer && c.status === 'running' && (
              <button type="button" className="arc-btn arc-btn-sm" disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id, status: 'paused' })}>Pause</button>
            )}
            {writer && c.status !== 'finished' && c.status !== 'draft' && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={setStatus.isPending}
                onClick={() => { void confirmAction({ title: `Finish ${c.name}?`, body: 'It stops for good; its calls and results are kept.', action: 'Finish', danger: true }).then((ok) => { if (ok) setStatus.mutate({ id, status: 'finished' }); }); }}>
                Finish
              </button>
            )}
            {writer && c.status !== 'running' && (
              <RowAction kind="delete" label={`Delete ${c.name}`} disabled={del.isPending}
                onClick={() => { void confirmAction({ title: `Delete ${c.name} and its call history?`, body: 'Its leads stay on their lists.', action: 'Delete', danger: true }).then((ok) => { if (ok) del.mutate(id, { onSuccess: onGone }); }); }} />
            )}
          </div>
        ) : undefined} />
      {setStatus.error && <ErrorBanner error={setStatus.error} />}
      {c.status !== 'running' && c.missing.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} /><span>Before it can run, it needs {c.missing.join(', ')}.</span>
        </div>
      )}

      <Tabs style={{ marginBottom: 14 }}>
        <Tab active={tab === 'overview'} onSelect={() => go('overview')}>Overview</Tab>
        <Tab active={tab === 'calls'} onSelect={() => go('calls')}>Calls</Tab>
        {writer && <Tab active={tab === 'settings'} onSelect={() => go('settings')}>Settings</Tab>}
      </Tabs>

      {tab === 'overview' && (
        <StatGroup>
          <Stat bare icon="phone" hue={1} label="Ready now" value={formatNumber(c.counts.due_now)} sub="can be called now" />
          <Stat bare icon="clock" hue={2} label="Waiting" value={formatNumber(Math.max(0, c.counts.dialable - c.counts.due_now))} sub="to be tried again later" />
          <Stat bare icon="headset" hue={3} label="With agents" value={formatNumber(c.counts.leased)} />
          <Stat bare icon="check" hue={4} label="Done" value={formatNumber(c.counts.done)} sub={`of ${formatNumber(c.counts.total)}`} />
          <Stat bare icon="close" hue={5} label="Do not call" value={formatNumber(c.counts.suppressed)} />
        </StatGroup>
      )}
      {tab === 'calls' && <Calls tenantId={tenantId} id={id} />}
      {tab === 'settings' && writer && (
        <Editor tenantId={tenantId} campaign={c} onSaved={(name) => { if (name !== c.name) onRenamed(name); }} />
      )}
    </>
  );
}

function Calls({ tenantId, id }: { tenantId: string; id: string }) {
  const q = useCampaignAttempts(tenantId, id);
  return (
    <div className="arc-card">
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead><tr><th>When</th><th>Lead</th><th>Agent</th><th>Outcome</th><th>Disposition</th><th>Note</th></tr></thead>
          {q.data === undefined ? <TableSkeleton cols={6} /> : (
            <tbody>
              {q.data.data.map((a) => (
                <tr key={a.id}>
                  <td className="arc-num" title={new Date(a.started_at).toLocaleString('en-ZA')}>{relativeTime(a.started_at)}</td>
                  <td>{[a.first_name, a.last_name].filter(Boolean).join(' ') || '—'}<div className="arc-mono arc-subtle" style={{ fontSize: 11.5 }}>+{a.phone}</div></td>
                  <td>{a.agent_name ?? a.agent_number ?? '—'}</td>
                  <td>{a.ended_at ? (a.outcome ?? '—') : <Badge tone="info">on the call</Badge>}{a.amd_result && <div className="arc-subtle" style={{ fontSize: 11.5 }} title={a.amd_phrase ?? undefined}>
                    {a.amd_result === 'SCREENER' ? 'call screen' : a.amd_result === 'HUMAN' ? 'a person' : a.amd_result === 'MACHINE' ? 'a machine' : 'not sure'}
                    {a.amd_engine === 'ai' ? ' (AI' + (a.amd_charge ? `, ${formatMoney(a.amd_charge, 'ZAR')}` : '') + ')' : ''}
                    {a.amd_phrase ? `: “${a.amd_phrase}”` : ''}</div>}</td>
                  <td>{a.disposition ?? '—'}</td>
                  <td style={{ maxWidth: 280 }}>{a.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {q.data && q.data.data.length === 0 && <Empty icon="phone" title="No calls yet" />}
    </div>
  );
}

// ================================================================================== the editor

const DAYS: ReadonlyArray<readonly [number, string]> = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat']];
const OUTCOME_LABEL: Record<string, string> = { busy: 'Busy', no_answer: 'No answer', machine: 'Answering machine', failed: 'Failed to connect' };

function Editor({ tenantId, campaign, onSaved }: { tenantId: string; campaign: Campaign | null; onSaved: (name: string) => void }) {
  const save = useSaveCampaign(tenantId);
  const lists = useDiallerLists(tenantId);
  const exts = useExtensions(tenantId, { limit: 200 });
  const numbers = useNumbers(tenantId);
  const queues = useQueues(tenantId);
  const groups = useRingGroups(tenantId);
  const ivrs = useIvrs(tenantId);
  const scripts = useScripts(tenantId);
  const pools = useQuery({
    queryKey: ['number-pools', tenantId],
    queryFn: () => api.get<{ data: NumberPool[] }>(`/v1/tenants/${tenantId}/number-pools`),
  });
  // What an AI answering-machine check costs (0135), to say so where it is switched on.
  const prices = useQuery({
    queryKey: ['prices'],
    queryFn: () => api.get<{ data: Array<{ key: string; amount: string; currency: string }> }>('/v1/prices'),
    staleTime: 10 * 60_000,
  });
  const ai = prices.data?.data.find((p) => p.key === 'amd_ai_check');
  const aiPrice = ai ? formatMoney(ai.amount, ai.currency) : 'costs per';
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string }> }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: Array<{ code: string; label: string; is_active: boolean }> }>(`/v1/tenants/${tenantId}/disposition-codes`),
    enabled: !!tenantId,
  });

  const [f, setF] = useState(() => ({
    name: campaign?.name ?? '',
    description: campaign?.description ?? '',
    mode: (campaign?.mode === 'auto' ? 'auto' : 'preview') as 'preview' | 'auto',
    window_start: campaign?.window_start ?? '08:00',
    window_end: campaign?.window_end ?? '20:00',
    window_days: campaign?.window_days ?? [1, 2, 3, 4, 5, 6],
    lead_order: campaign?.lead_order ?? 'oldest',
    // One select for the caller ID (0137): '' the extension's own, n:<digits> one number, p:<id> a pool.
    cli: campaign?.cli_pool_id ? `p:${campaign.cli_pool_id}` : campaign?.caller_id ? `n:${campaign.caller_id}` : '',
    max_attempts: campaign?.max_attempts ?? 8,
    recycle: (campaign?.recycle ?? DEFAULT_RECYCLE) as RecycleRules,
    sticky_agent_days: campaign?.sticky_agent_days ?? null as number | null,
    list_ids: campaign?.list_ids ?? [],
    agent_extension_ids: campaign?.agent_extension_ids ?? [],
    billing_extension_id: campaign?.billing_extension_id ?? '',
    message_media_id: campaign?.message_media_id ?? '',
    transfer: campaign?.transfer_destination ? `${campaign.transfer_destination.type}:${campaign.transfer_destination.id}` : '',
    transfer_key: campaign?.transfer_key ?? '1',
    optout_key: campaign?.optout_key ?? '9',
    max_lines: campaign?.max_lines ?? 5,
    amd_mode: campaign?.amd_mode ?? 'off',
    on_machine: campaign?.on_machine ?? 'hangup',
    screen_reply_media_id: campaign?.screen_reply_media_id ?? '',
    agent_priorities: campaign?.agent_priorities ?? {} as Record<string, number>,
    offer_account_dispositions: campaign?.offer_account_dispositions ?? true,
    dispositions: campaign?.dispositions ?? [] as CampaignDisposition[],
    note_required: campaign?.note_required ?? false,
    reference_label: campaign?.reference_label ?? '',
    reference_required: campaign?.reference_required ?? false,
    tags: (campaign?.tags ?? []).join(', '),
    tags_required: campaign?.tags_required ?? false,
    script_id: campaign?.script_id ?? '',
  }));
  useEffect(() => { save.reset(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const pool = f.cli.startsWith('p:') ? (pools.data?.data ?? []).find((p) => p.id === f.cli.slice(2)) : undefined;
  const cliHint = pool
    ? `${pool.numbers.length} numbers, ${pool.mode === 'rotate' ? 'in order' : 'at random'}, ${POOL_PERIOD_LABEL[pool.period].toLowerCase()}${pool.current ? `. Now ${pool.current}` : ''}.`
    : f.cli ? 'Every call shows this number.' : 'Pools are made under Numbers › Number pools.';

  const seats = (exts.data?.data ?? []).map((e) => ({ id: e.id, label: `${e.number} ${e.display_name}` }));
  // Agents: seats whose package includes the dialler (0130); any seat can be billed for an auto campaign.
  const dialSeats = (exts.data?.data ?? []).filter((e) => e.may_dial !== false).map((e) => ({ id: e.id, label: `${e.number} ${e.display_name}` }));
  const noDial = (exts.data?.data ?? []).filter((e) => e.may_dial === false).length;
  const ruleKeys = useMemo(() => [
    ...Object.keys(OUTCOME_LABEL).map((k) => ({ key: k, label: OUTCOME_LABEL[k]! })),
    ...(codes.data?.data ?? []).filter((c) => c.is_active).map((c) => ({ key: c.code, label: c.label })),
  ], [codes.data]);
  const unused = ruleKeys.filter((k) => !f.recycle[k.key]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const [ttype, tid] = f.transfer.split(':');
    save.mutate({
      ...(campaign ? { id: campaign.id } : {}),
      name: f.name.trim(), description: f.description.trim() || null, mode: f.mode,
      window_start: f.window_start, window_end: f.window_end, window_days: f.window_days, lead_order: f.lead_order,
      caller_id: f.cli.startsWith('n:') ? f.cli.slice(2) : null,
      cli_pool_id: f.cli.startsWith('p:') ? f.cli.slice(2) : null, max_attempts: f.max_attempts, recycle: f.recycle,
      sticky_agent_days: f.sticky_agent_days, list_ids: f.list_ids, agent_extension_ids: f.agent_extension_ids,
      billing_extension_id: f.billing_extension_id || null, message_media_id: f.message_media_id || null,
      transfer_destination: ttype && tid ? { type: ttype, id: tid } : null,
      transfer_key: f.transfer_key, optout_key: f.optout_key, max_lines: f.max_lines,
      amd_mode: f.amd_mode, on_machine: f.on_machine,
      screen_reply_media_id: f.amd_mode === 'own_then_ai' || f.amd_mode === 'ai' ? f.screen_reply_media_id || null : null,
      agent_priorities: Object.fromEntries(f.agent_extension_ids.map((e) => [e, f.agent_priorities[e] ?? 5])),
      offer_account_dispositions: f.offer_account_dispositions,
      dispositions: f.dispositions.filter((d) => d.label.trim()).map((d) => ({ ...d, label: d.label.trim() })),
      note_required: f.note_required,
      reference_label: f.reference_label.trim() || null,
      reference_required: f.reference_required && !!f.reference_label.trim(),
      tags: [...new Set(f.tags.split(',').map((t) => t.trim()).filter(Boolean))],
      tags_required: f.tags_required,
      script_id: f.mode === 'preview' ? f.script_id || null : null,
    }, { onSuccess: () => onSaved(f.name.trim()) });
  };

  return (
    <form className="arc-card arc-card-body" onSubmit={submit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-name">Name</label>
          <input id="cp-name" className="arc-input" value={f.name} maxLength={120} required onChange={(e) => set('name', e.target.value)} placeholder="March renewals" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <span className="arc-label">Mode</span>
          <Segmented label="Mode" value={f.mode} onChange={(v) => set('mode', v)}
            options={[['preview', 'Preview: agents call'], ['auto', 'Auto: the switch calls']] as const} />
        </div>
        <div className="arc-field" style={{ margin: 0, gridColumn: '1 / -1' }}>
          <label className="arc-label" htmlFor="cp-desc">Description (optional)</label>
          <input id="cp-desc" className="arc-input" value={f.description} maxLength={500} onChange={(e) => set('description', e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-cli">Caller ID</label>
          <select id="cp-cli" className="arc-select" value={f.cli} onChange={(e) => set('cli', e.target.value)}>
            <option value="">The extension's own</option>
            {(pools.data?.data ?? []).length > 0 && (
              <optgroup label="Number pool">
                {(pools.data?.data ?? []).map((p) => <option key={p.id} value={`p:${p.id}`}>{p.name}</option>)}
              </optgroup>
            )}
            <optgroup label="One number">
              {(numbers.data?.data ?? []).map((n) => <option key={n.id} value={`n:${n.e164.replace(/^\+/, '')}`}>{n.e164}</option>)}
            </optgroup>
          </select>
          <div className="arc-hint">{cliHint}</div>
        </div>
      </div>

      <SubHead>Leads</SubHead>
      <OrderedPicker items={(lists.data?.data ?? []).map((l) => ({ id: l.id, label: l.name, detail: `${formatNumber(l.records)} leads` }))}
        value={f.list_ids} onChange={(v) => set('list_ids', v)} emptyText="No lists yet." />
      <div className="arc-row" style={{ gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-order">Order</label>
          <select id="cp-order" className="arc-select" value={f.lead_order} onChange={(e) => set('lead_order', e.target.value as typeof f.lead_order)}>
            <option value="oldest">Oldest first</option><option value="newest">Newest first</option><option value="random">Random</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-max">Calls per contact, at most</label>
          <input id="cp-max" className="arc-input arc-num" type="number" min={1} max={50} value={f.max_attempts}
            onChange={(e) => set('max_attempts', Number(e.target.value) || 1)} style={{ width: 100 }} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-sticky">Same agent for the same lead</label>
          <select id="cp-sticky" className="arc-select" value={f.sticky_agent_days ?? ''}
            onChange={(e) => set('sticky_agent_days', e.target.value ? Number(e.target.value) : null)}>
            <option value="">Off</option><option value="7">For 7 days</option><option value="30">For 30 days</option><option value="90">For 90 days</option>
          </select>
        </div>
      </div>

      <SubHead>When</SubHead>
      <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-from">From</label>
          <input id="cp-from" className="arc-input" type="time" value={f.window_start} onChange={(e) => set('window_start', e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-to">To</label>
          <input id="cp-to" className="arc-input" type="time" value={f.window_end} onChange={(e) => set('window_end', e.target.value)} />
        </div>
        <div className="arc-row" style={{ gap: 4 }} role="group" aria-label="Days">
          {DAYS.map(([d, label]) => (
            <button key={d} type="button" className={`arc-btn arc-btn-sm ${f.window_days.includes(d) ? 'arc-btn-primary' : ''}`}
              onClick={() => set('window_days', f.window_days.includes(d) ? f.window_days.filter((x) => x !== d) : [...f.window_days, d].sort())}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="arc-hint" style={{ marginTop: 6 }}>
        Always inside the legal hours: Monday to Friday 08:00–20:00, Saturday 09:00–13:00, never Sundays or public holidays.
      </p>

      <SubHead sub="A try calls each of a contact's numbers in order, straight away. Not reached on any of them, and the
        contact waits this long for its next try. How many tries it gets is set on its lead list.">Wait before the next try</SubHead>
      <table className="arc-table" style={{ maxWidth: 480 }}>
        <thead><tr><th>When the last call was</th><th>Wait</th><th /></tr></thead>
        <tbody>
          {Object.entries(f.recycle).map(([k, r]) => (
            <tr key={k}>
              <td>{ruleKeys.find((x) => x.key === k)?.label ?? k}</td>
              <td>
                <select className="arc-select" aria-label={`Wait after ${k}`} value={r.after_min}
                  onChange={(e) => set('recycle', { ...f.recycle, [k]: { ...r, after_min: Number(e.target.value) } })}>
                  {[5, 15, 20, 30, 60, 120, 180, 360, 720, 1440, 2880, 4320].map((m) => (
                    <option key={m} value={m}>{m < 60 ? `${m} min` : m < 1440 ? `${m / 60} h` : `${m / 1440} days`}</option>
                  ))}
                </select>
              </td>
              <td className="arc-td-right">
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label={`Default wait after ${k}`}
                  onClick={() => { const next = { ...f.recycle }; delete next[k]; set('recycle', next); }}><Icon name="close" size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {unused.length > 0 && (
        <select className="arc-select" style={{ maxWidth: 260, marginTop: 8 }} value="" aria-label="Add a retry rule"
          onChange={(e) => { if (e.target.value) set('recycle', { ...f.recycle, [e.target.value]: { after_min: 1440 } }); }}>
          <option value="">Add a wait for…</option>
          {unused.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
      )}
      <p className="arc-hint" style={{ marginTop: 6 }}>Anything not listed waits 3 hours.</p>

      {f.mode === 'preview' && (
        <>
          <SubHead>Agents</SubHead>
          <OrderedPicker items={dialSeats} value={f.agent_extension_ids} onChange={(v) => set('agent_extension_ids', v)}
            emptyText={noDial ? 'No extension has the dialler in its package yet.' : 'Nobody yet.'} />
          {f.agent_extension_ids.length > 0 && (
            <div className="arc-stack" style={{ gap: 6, marginTop: 10 }}>
              <span className="arc-label">Priority, for agents in more than one campaign</span>
              <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                {f.agent_extension_ids.map((id) => (
                  <label key={id} className="arc-row" style={{ gap: 6, fontSize: 13, padding: '4px 8px', border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
                    <span>{dialSeats.find((x) => x.id === id)?.label ?? 'An extension'}</span>
                    <select className="arc-select" style={{ width: 'auto', padding: '2px 6px' }} aria-label="Priority" value={f.agent_priorities[id] ?? 5}
                      onChange={(e) => set('agent_priorities', { ...f.agent_priorities, [id]: Number(e.target.value) })}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n === 1 ? '1 (first)' : n === 9 ? '9 (last)' : n}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          )}
          <p className="arc-hint" style={{ marginTop: 6 }}>
            An agent in several campaigns starts on their priority 1 campaign that has someone to call, and can switch.
            Agents call from their own extension, with its caller ID.
            {noDial > 0 && ` ${noDial} extension${noDial === 1 ? '' : 's'} without the dialler in ${noDial === 1 ? 'its' : 'their'} package ${noDial === 1 ? 'is' : 'are'} not listed.`}
          </p>
          <div className="arc-field" style={{ margin: '14px 0 0', maxWidth: 320 }}>
            <label className="arc-label" htmlFor="cp-script">Call script</label>
            <select id="cp-script" className="arc-select" value={f.script_id} onChange={(e) => set('script_id', e.target.value)}>
              <option value="">None</option>
              {(scripts.data?.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="arc-hint">Shown to agents beside each call, filled in from the lead. Made under Call scripts.</div>
          </div>

          <SubHead>Dispositions</SubHead>
          <Toggle id="cp-accdisp" label="Offer the account’s dispositions too" hint="The ones under Agent codes. Off: only this campaign’s own."
            checked={f.offer_account_dispositions} onChange={(v) => set('offer_account_dispositions', v)} />
          <div className="arc-stack" style={{ gap: 6, marginTop: 8 }}>
            {f.dispositions.map((d, i) => {
              const upd = (patch: Partial<CampaignDisposition>) => set('dispositions', f.dispositions.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              return (
                <div key={i} className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <input className="arc-input" style={{ maxWidth: 220 }} placeholder="e.g. Appointment booked" value={d.label} aria-label="Disposition"
                    onChange={(e) => upd({ label: e.target.value, ...(campaign?.dispositions?.some((x) => x.code === d.code) ? {} : { code: codeOf(e.target.value, f.dispositions, i) }) })} />
                  <select className="arc-select" style={{ width: 'auto' }} aria-label="Kind" value={d.kind} onChange={(e) => upd({ kind: e.target.value as CampaignDisposition['kind'] })}>
                    <option value="positive">Positive</option><option value="neutral">Neutral</option><option value="negative">Negative</option>
                  </select>
                  <select className="arc-select" style={{ width: 'auto' }} aria-label="Then" value={d.effect} onChange={(e) => upd({ effect: e.target.value as CampaignDisposition['effect'] })}>
                    <option value="final">Done with this lead</option><option value="retry">Try again later</option><option value="next_number">Try their next number</option>
                  </select>
                  <label className="arc-row" style={{ gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={d.requires_note} onChange={(e) => upd({ requires_note: e.target.checked })} /> Needs a note
                  </label>
                  <RowAction kind="delete" label="Remove disposition" onClick={() => set('dispositions', f.dispositions.filter((_, j) => j !== i))} />
                </div>
              );
            })}
            <div>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => set('dispositions', [...f.dispositions,
                { code: codeOf('outcome', f.dispositions, f.dispositions.length), label: '', kind: 'neutral', effect: 'final', requires_note: false }])}>
                <Icon name="plus" size={12} /> Campaign disposition
              </button>
            </div>
          </div>

          <SubHead>Before a call is done</SubHead>
          <div className="arc-stack" style={{ gap: 10, maxWidth: 560 }}>
            <Toggle id="cp-note" label="A note on every answered call" checked={f.note_required} onChange={(v) => set('note_required', v)} />
            <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="arc-field" style={{ margin: 0, flex: '1 1 220px' }}>
                <label className="arc-label" htmlFor="cp-ref">Reference to capture</label>
                <input id="cp-ref" className="arc-input" maxLength={40} placeholder="e.g. Policy number (leave empty for none)" value={f.reference_label}
                  onChange={(e) => set('reference_label', e.target.value)} />
              </div>
              <label className="arc-row" style={{ gap: 6, fontSize: 13, paddingBottom: 8 }}>
                <input type="checkbox" disabled={!f.reference_label.trim()} checked={f.reference_required && !!f.reference_label.trim()}
                  onChange={(e) => set('reference_required', e.target.checked)} /> Required
              </label>
            </div>
            <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="arc-field" style={{ margin: 0, flex: '1 1 220px' }}>
                <label className="arc-label" htmlFor="cp-tags">Tags agents choose from</label>
                <input id="cp-tags" className="arc-input" placeholder="e.g. Hot lead, Price objection, Competitor" value={f.tags}
                  onChange={(e) => set('tags', e.target.value)} />
                <div className="arc-hint">Separated by commas.</div>
              </div>
              <label className="arc-row" style={{ gap: 6, fontSize: 13, paddingBottom: 26 }}>
                <input type="checkbox" disabled={!f.tags.trim()} checked={f.tags_required && !!f.tags.trim()}
                  onChange={(e) => set('tags_required', e.target.checked)} /> At least one
              </label>
            </div>
          </div>
        </>
      )}

      {f.mode === 'auto' && (
        <>
          <SubHead>The call</SubHead>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="cp-msg">Message</label>
              <select id="cp-msg" className="arc-select" value={f.message_media_id} onChange={(e) => set('message_media_id', e.target.value)}>
                <option value="">Choose a recording…</option>
                {(media.data?.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <div className="arc-hint">Say who is calling and the number to call back in the first sentence.</div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="cp-xfer">Press {f.transfer_key} to speak to someone</label>
              <select id="cp-xfer" className="arc-select" value={f.transfer} onChange={(e) => set('transfer', e.target.value)}>
                <option value="">Choose…</option>
                {(queues.data?.data ?? []).map((q) => <option key={q.id} value={`queue:${q.id}`}>Queue: {q.name}</option>)}
                {(groups.data?.data ?? []).map((g) => <option key={g.id} value={`ring_group:${g.id}`}>Ring group: {g.name}</option>)}
                {(ivrs.data?.data ?? []).map((m) => <option key={m.id} value={`ivr:${m.id}`}>Menu: {m.name}</option>)}
              </select>
            </div>
            <div className="arc-row" style={{ gap: 10 }}>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="cp-tk">Transfer key</label>
                <input id="cp-tk" className="arc-input arc-mono" maxLength={1} value={f.transfer_key} style={{ width: 60 }} onChange={(e) => set('transfer_key', e.target.value)} />
              </div>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="cp-ok">Opt-out key</label>
                <input id="cp-ok" className="arc-input arc-mono" maxLength={1} value={f.optout_key} style={{ width: 60 }} onChange={(e) => set('optout_key', e.target.value)} />
              </div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="cp-bill">Bill the calls to</label>
              <select id="cp-bill" className="arc-select" value={f.billing_extension_id} onChange={(e) => set('billing_extension_id', e.target.value)}>
                <option value="">Choose an extension…</option>
                {seats.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <div className="arc-hint">Its dialling rules and the account's credit apply to every call.</div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="cp-lines">Calls at once, at most</label>
              <input id="cp-lines" className="arc-input arc-num" type="number" min={1} max={200} value={f.max_lines} style={{ width: 100 }}
                onChange={(e) => set('max_lines', Number(e.target.value) || 1)} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="cp-amd">Answering machine detection</label>
              <select id="cp-amd" className="arc-select" value={f.amd_mode} onChange={(e) => set('amd_mode', e.target.value as typeof f.amd_mode)}>
                <option value="off">Off</option>
                <option value="own">On (free)</option>
                <option value="own_then_ai">On, with an AI check when unsure ({aiPrice} a check)</option>
                <option value="ai">AI check on every call ({aiPrice} a check)</option>
              </select>
              {(f.amd_mode === 'own_then_ai' || f.amd_mode === 'ai') && (
                <div className="arc-hint">
                  The first seconds are transcribed and checked for voicemail and call-screen phrases. {aiPrice} a check,
                  charged to the balance; free if the account uses its own Deepgram key.
                </div>
              )}
            </div>
            {f.amd_mode !== 'off' && (
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="cp-om">When a machine answers</label>
                <select id="cp-om" className="arc-select" value={f.on_machine} onChange={(e) => set('on_machine', e.target.value as typeof f.on_machine)}>
                  <option value="hangup">Hang up and try again later</option>
                  <option value="message">Leave the message after the beep</option>
                </select>
              </div>
            )}
            {(f.amd_mode === 'own_then_ai' || f.amd_mode === 'ai') && (
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="cp-screen">When a phone screens the call</label>
                <select id="cp-screen" className="arc-select" value={f.screen_reply_media_id} onChange={(e) => set('screen_reply_media_id', e.target.value)}>
                  <option value="">Treat it like a machine</option>
                  {(media.data?.data ?? []).map((m) => <option key={m.id} value={m.id}>Reply with: {m.name}</option>)}
                </select>
                <div className="arc-hint">
                  Apple and Google phones can answer unknown callers and ask who is calling. The reply should say
                  who you are and why you are calling; if the person picks up, they go to an agent.
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="arc-row" style={{ gap: 10, marginTop: 18 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !f.name.trim()}>
          {save.isPending ? 'Saving…' : campaign ? 'Save' : 'Make the campaign'}
        </button>
        {save.isSuccess && <span className="arc-muted" style={{ fontSize: 12.5 }}>Saved</span>}
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}
